import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { mercury, thunderBird } from '@parchment/atoms';
import { ValidationRequiredIcon } from '@parchment/icons';
import { SCALE, VALIDATION_STATES } from '../../utils/constants';
import Skeleton from 'react-loading-skeleton';
const noop = () => undefined;
export default function FieldLabel(props) {
  const labelRef = useRef();
  const {
    children,
    label,
    internalLabel,
    onLabelText = noop,
    ...remainingProps
  } = props;
  const child = /*#__PURE__*/React.cloneElement(children, remainingProps);
  useEffect(() => {
    if (!label) return;
    // note: jsdom doesn't support innerText, but does textContent.
    // innerText should produce more appropriate text in regards to
    // spacing, where as textContent is very literal in what it reads.
    onLabelText(labelRef.current.innerText || labelRef.current.textContent);
  }, [label]);
  if (!label) {
    return child;
  }
  const displayLabel = props.required ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(ValidationRequired, {
    "aria-hidden": false,
    contextType: "text"
  })), label) : label;
  return /*#__PURE__*/React.createElement(FieldLabelWrapper, null, /*#__PURE__*/React.createElement(LabelWrapper, {
    scale: props.scale,
    internalLabel: internalLabel,
    ref: labelRef
  }, /*#__PURE__*/React.createElement(StyledLabel, {
    className: props.className,
    htmlFor: props.id,
    htmlForm: props.htmlForm,
    scale: props.scale
  }, props.loading ? /*#__PURE__*/React.createElement(Skeleton, {
    width: `${Math.floor(Math.random() * 50) + 50}%`,
    style: {
      maxWidth: '200px'
    }
  }) : displayLabel)), child);
}
FieldLabel.enums = {
  validationStates: VALIDATION_STATES,
  scale: SCALE
};
FieldLabel.propTypes = {
  id: PropTypes.string.isRequired,
  //used to determine what child its labeling (id, for)
  className: PropTypes.string,
  mode: PropTypes.oneOf([SCALE.small, SCALE.medium, SCALE.large]),
  valid: PropTypes.oneOf(Object.values(FieldLabel.enums.validationStates)),
  scale: PropTypes.oneOf(Object.values(FieldLabel.enums.scale)),
  required: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  label: PropTypes.node,
  htmlForm: PropTypes.string,
  role: PropTypes.string,
  internalLabel: PropTypes.bool,
  /**
   * Method that is invoked after extracting the label as text
   */
  onLabelText: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
  loading: PropTypes.bool
};
FieldLabel.defaultProps = {
  scale: SCALE.medium,
  internalLabel: false
};
const FieldLabelWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "FieldLabel__FieldLabelWrapper",
  componentId: "sc-1f5rwum-0"
})(["display:block;height:100%;"]);
const ValidationRequired = /*#__PURE__*/styled(ValidationRequiredIcon).withConfig({
  displayName: "FieldLabel__ValidationRequired",
  componentId: "sc-1f5rwum-1"
})(["color:", ";"], thunderBird);
const LabelWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "FieldLabel__LabelWrapper",
  componentId: "sc-1f5rwum-2"
})(["display:", ";margin-bottom:", ";"], props => props.internalLabel ? 'none' : 'block', props => ({
  small: '5px',
  medium: '8px',
  large: '10px'
})[props.scale]);
const StyledLabel = /*#__PURE__*/styled.label.withConfig({
  displayName: "FieldLabel__StyledLabel",
  componentId: "sc-1f5rwum-3"
})(["font-size:", ";font-weight:600;white-space:nowrap;overflow:hidden;display:flex;align-items:center;> div:last-of-type{margin-left:auto;}@media (prefers-color-scheme:dark){body.use-system-color-scheme &{color:", ";}}"], props => ({
  small: '13px',
  medium: '16px',
  large: '20px'
})[props.scale], mercury);